import { useEffect, useState } from 'react';
import {
  createOutboundSlot,
  deleteOutboundSlot,
  listOutbound,
  updateOutboundSlot,
  type IntegrationType,
  type OutboundEntry,
  type OutboundSlot,
  type OutboundSlotInput,
  type PricingMode,
} from '../lib/api';

export default function OutboundTokens() {
  const [entries, setEntries] = useState<OutboundEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    reload();
  }, []);

  async function reload() {
    try {
      const response = await listOutbound();
      setEntries(response.outbound);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load outbound credentials.');
    }
  }

  return (
    <section className="block-brutal ">
      <header className="px-6 py-5 border-b-2 border-border flex items-baseline justify-between">
        <div>
          <h2 className="font-display text-xl uppercase">Outbound credentials</h2>
          <p className="text-sm text-muted-foreground mt-1">
            One or more credentials per integration. Each can be an{' '}
            <strong>API key</strong> (billed per token) or a{' '}
            <strong>Subscription</strong> (flat monthly fee, allocated pro-rata
            across the inbound tokens that route through it).
          </p>
        </div>
        <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-spray-magenta font-bold">
          // upstream
        </div>
      </header>

      {error && (
        <div className="px-6 py-4 border-b-2 border-border bg-muted text-sm text-foreground font-mono">
          {error}
        </div>
      )}

      <ul>
        {(entries ?? []).map((entry) => (
          <IntegrationCard key={entry.id} entry={entry} onChanged={reload} />
        ))}
        {entries === null && (
          <li className="px-6 py-6 text-sm text-muted-foreground font-mono">Loading…</li>
        )}
      </ul>
    </section>
  );
}

function IntegrationCard({
  entry,
  onChanged,
}: {
  entry: OutboundEntry;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);

  return (
    <li className="px-6 py-5 border-b-2 border-border last:border-b-0">
      <div className="flex items-baseline justify-between gap-4 mb-2">
        <div>
          <div className="text-lg text-foreground">{entry.label}</div>
          <div className="text-sm text-muted-foreground mt-0.5 max-w-xl">
            {entry.outboundHint}
          </div>
        </div>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="text-sm font-medium bg-spray-yellow text-primary-foreground px-4 py-2  hover:bg-primary transition flex-shrink-0"
          >
            + Add credential
          </button>
        )}
      </div>

      {entry.slots.length === 0 && !adding && (
        <div className="mt-2 text-sm text-muted-foreground italic">
          No credentials configured. Add one to start routing inbound tokens
          through this integration.
        </div>
      )}

      {entry.slots.length > 0 && (
        <ul className="mt-3 border border-border  overflow-hidden">
          {entry.slots.map((slot, index) => (
            <SlotRow
              key={slot.slot}
              integrationId={entry.id}
              placeholder={entry.outboundPlaceholder}
              slot={slot}
              isLast={index === entry.slots.length - 1}
              onChanged={onChanged}
            />
          ))}
        </ul>
      )}

      {adding && (
        <SlotForm
          integrationId={entry.id}
          placeholder={entry.outboundPlaceholder}
          mode="create"
          onSaved={() => {
            setAdding(false);
            onChanged();
          }}
          onCancel={() => setAdding(false)}
        />
      )}
    </li>
  );
}

function SlotRow({
  integrationId,
  placeholder,
  slot,
  isLast,
  onChanged,
}: {
  integrationId: IntegrationType;
  placeholder: string;
  slot: OutboundSlot;
  isLast: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  async function handleRemove() {
    if (!confirm(`Remove "${slot.label}"? Inbound tokens routed to this slot will stop working.`)) return;
    setBusy(true);
    try {
      await deleteOutboundSlot(integrationId, slot.slot);
    } finally {
      setBusy(false);
      onChanged();
    }
  }

  const modeLabel = slot.pricingMode === 'subscription'
    ? `Subscription · $${(slot.monthlyFeeCents / 100).toFixed(2)}/mo`
    : 'API key · per-token';

  return (
    <li
      className={
        'px-4 py-3 flex items-center justify-between gap-4 ' +
        (isLast ? '' : 'border-b-2 border-border')
      }
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium text-foreground truncate">{slot.label}</span>
          <span className="font-mono text-[10.5px] tracking-wider uppercase text-spray-yellow bg-muted px-1.5 py-0.5 rounded">
            slot {slot.slot}
          </span>
        </div>
        <div className="font-mono text-xs text-muted-foreground mt-0.5">
          {slot.preview} · {modeLabel}
        </div>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        <button
          onClick={() => setEditing(true)}
          disabled={busy || editing}
          className="text-xs font-medium px-3 py-1.5  border border-border hover:border-foreground transition disabled:opacity-60"
        >
          Replace
        </button>
        <button
          onClick={handleRemove}
          disabled={busy}
          className="text-xs font-medium px-3 py-1.5  text-muted-foreground hover:text-foreground transition disabled:opacity-60"
        >
          Remove
        </button>
      </div>
      {editing && (
        <SlotForm
          integrationId={integrationId}
          placeholder={placeholder}
          mode="update"
          slot={slot}
          onSaved={() => {
            setEditing(false);
            onChanged();
          }}
          onCancel={() => setEditing(false)}
        />
      )}
    </li>
  );
}

function SlotForm({
  integrationId,
  placeholder,
  mode,
  slot,
  onSaved,
  onCancel,
}: {
  integrationId: IntegrationType;
  placeholder: string;
  mode: 'create' | 'update';
  slot?: OutboundSlot;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [credential, setCredential] = useState('');
  const [label, setLabel] = useState(slot?.label ?? '');
  const [pricingMode, setPricingMode] = useState<PricingMode>(
    slot?.pricingMode ?? 'api_key',
  );
  const [monthlyFeeUsd, setMonthlyFeeUsd] = useState(
    slot ? String((slot.monthlyFeeCents / 100).toFixed(2)) : '',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!credential.trim() || !label.trim()) return;
    setSaving(true);
    setError(null);
    const input: OutboundSlotInput = {
      credential: credential.trim(),
      label: label.trim(),
      pricingMode,
      monthlyFeeCents:
        pricingMode === 'subscription'
          ? Math.round(parseFloat(monthlyFeeUsd || '0') * 100)
          : 0,
    };
    try {
      if (mode === 'create') {
        await createOutboundSlot(integrationId, input);
      } else if (slot) {
        await updateOutboundSlot(integrationId, slot.slot, input);
      }
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-4 w-full bg-card border border-border  p-4 flex flex-col gap-3"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-mono tracking-wide uppercase text-muted-foreground">
            Label
          </span>
          <input
            type="text"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="e.g. Personal Max, Agency API key"
            className="border-2 border-border bg-card  px-4 py-2 text-sm focus:outline-none focus:border-foreground"
            autoFocus={mode === 'create'}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-mono tracking-wide uppercase text-muted-foreground">
            Pricing mode
          </span>
          <select
            value={pricingMode}
            onChange={(event) => setPricingMode(event.target.value as PricingMode)}
            className="border-2 border-border bg-card  px-4 py-2 text-sm font-mono focus:outline-none focus:border-foreground"
          >
            <option value="api_key">API key · per-token billing</option>
            <option value="subscription">Subscription · flat monthly fee</option>
          </select>
        </label>
      </div>
      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-mono tracking-wide uppercase text-muted-foreground">
          {mode === 'update' ? 'New credential (will replace the existing one)' : 'Credential'}
        </span>
        <input
          type="password"
          value={credential}
          onChange={(event) => setCredential(event.target.value)}
          placeholder={placeholder}
          className="border-2 border-border bg-card  px-4 py-2.5 font-mono text-sm focus:outline-none focus:border-foreground"
        />
      </label>
      {pricingMode === 'subscription' && (
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-mono tracking-wide uppercase text-muted-foreground">
            Monthly fee (USD)
          </span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={monthlyFeeUsd}
            onChange={(event) => setMonthlyFeeUsd(event.target.value)}
            placeholder="200.00"
            className="border-2 border-border bg-card  px-4 py-2.5 font-mono text-sm focus:outline-none focus:border-foreground max-w-[200px]"
          />
          <span className="text-xs text-muted-foreground">
            Allocated pro-rata across inbound tokens that route through this
            slot, based on each token&rsquo;s share of monthly traffic.
          </span>
        </label>
      )}
      {error && <div className="text-sm text-spray-yellow font-mono">{error}</div>}
      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={saving || !credential.trim() || !label.trim()}
          className="bg-spray-yellow text-primary-foreground font-medium px-5 py-2.5  hover:bg-primary transition text-sm disabled:opacity-60"
        >
          {saving ? 'Saving…' : mode === 'create' ? 'Save credential' : 'Update'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-muted-foreground hover:text-foreground px-3 py-2 transition"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
